import React, { useState } from "react"
import { Box, Paper, Stack, Typography } from "@mui/material"
import GridRadioButton from "../../../../components/radio-button/grid-radio-button"
import CodeViewer from "../../../../components/CodeViewer/code-viewer"

const GridRadioButtonPage = () => {
    const [selected, setSelected] = useState("viewer")

    const optionsExample = [
        { value: "admin", label: "Administrador" },
        { value: "editor", label: "Editor" },
        { value: "viewer", label: "Visualizador" },
        { value: "guest", label: "Convidado" },
    ]

    const codeBasicUsage = `import GridRadioButton from "src/v2/components/GridRadioButton/grid-radio-button"

const options = [
  { value: "admin", label: "Administrador" },
  { value: "editor", label: "Editor" },
  { value: "viewer", label: "Visualizador" },
  { value: "guest", label: "Convidado" },
]

<GridRadioButton
  title="Nível de acesso"
  radioButtonItems={options}
  defaultRadioValue="viewer"
  columns={2}
  itemsPerColumn={2}
  direction="column"
  horizontalSpacing={{
    xlg: -12,
    lg: -10,
    md: -6,
    sm: -3,
    xxxs: 0,
  }}
  onChange={(event) => console.log(event.target.value)}
/>`

    const codePropsType = `type GridRadioButtonItem = {
  value: string
  label: string
}

type GridRadioButtonProps = {
  title?: string
  radioButtonItems: GridRadioButtonItem[]
  defaultRadioValue?: string
  columns?: number           // quantidade de colunas no grid
  itemsPerColumn?: number    // itens por coluna
  direction?: "row" | "column"
  spacing?: number | { mt?: number; mb?: number; ml?: number; mr?: number; pt?: number; pb?: number; pl?: number; pr?: number }
  horizontalSpacing?: number | Record<string, number> // espaçamento horizontal responsivo (columnSpacing do Grid). Suporta breakpoints xlg, lg, md, sm, xs, xxxs conforme tema.
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  // ...props adicionais são repassados para o RadioGroup / container
}`

    return (
        <Box sx={{ width: "100%" }}>
            <Stack spacing={3}>

                {/* INTRO + EXEMPLO PRINCIPAL */}
                <Paper>
                    <Box p={3}>
                        <Typography variant="h4" gutterBottom color="common.white">
                            {`GridRadioButton`}
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                            {`O componente GridRadioButton organiza opções de seleção única (radio)
em um layout de grid, permitindo distribuir as opções em múltiplas colunas.
É ideal para formulários com várias opções em que o layout em lista ficaria muito longo.`}
                        </Typography>

                        <Box mt={3}>
                            <Typography variant="subtitle1" gutterBottom color="common.white">
                                {`Exemplo principal`}
                            </Typography>

                            <Typography variant="body2" color="text.secondary" paragraph>
                                {`Exemplo com 2 colunas e 4 opções. A linha selecionada é controlada via estado local.`}
                            </Typography>

                            <Box mt={2}>
                                <GridRadioButton
                                    title="Nível de acesso"
                                    radioButtonItems={optionsExample}
                                    defaultRadioValue={selected}
                                    columns={2}
                                    itemsPerColumn={2}
                                    direction="column"
                                    onChange={(event) => setSelected(event.target.value)}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Paper>

                {/* USO BÁSICO */}
                <Paper>
                    <Box p={3}>
                        <Typography variant="h6" gutterBottom color="common.white">
                            {`Uso básico`}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" paragraph>
                            {`Abaixo um exemplo mínimo de configuração das opções, quantidade de colunas
e captura do valor selecionado no onChange.`}
                        </Typography>

                        <Paper variant="outlined">
                            <Box p={2}>
                                <CodeViewer code={codeBasicUsage} language="jsx" />
                            </Box>
                        </Paper>
                    </Box>
                </Paper>

                {/* PROPRIEDADES */}
                <Paper>
                    <Box p={3}>
                        <Typography variant="h6" gutterBottom color="common.white">
                            {`Propriedades do componente`}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" paragraph>
                            {`Principais propriedades expostas pelo GridRadioButton.
Propriedades adicionais são repassadas para o container interno (Grid / RadioGroup).`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                            {`O espaçamento horizontal pode ser número único ou objeto responsivo (ex.: { xlg: -12, lg: -10, md: -6, sm: -3, xxxs: 0 }), aplicado no columnSpacing do Grid interno.`}
                        </Typography>

                        <Paper variant="outlined">
                            <Box p={2}>
                                <Typography variant="subtitle2" gutterBottom color="common.white">
                                    {`Tipo das props (exemplo em TypeScript`}
                                    {`)`}
                                </Typography>
                                <CodeViewer code={codePropsType} language="tsx" />
                            </Box>
                        </Paper>
                    </Box>
                </Paper>

            </Stack>
        </Box>
    )
}

export default GridRadioButtonPage
